import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import type { TemplateResult } from 'lit';
import { getWorkshopByID } from '../api/workshop.js';
import { getWorkshopDocumentsByIDs } from '../api/workshop-document.js';
import { getInstitutionByID } from '../api/institution.js';
import { getUserByID } from '../api/users.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getParticipationByID } from '../api/participation-helper.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { NewWorkshopDocumentNotification } from '../entities/notification/NewWorkshopDocumentNotification.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { WorkshopDocument } from '../entities/WorkshopDocument.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { mongo } from '../server/mongo.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

defineMailTemplate(MailTemplateType.NEW_WORKSHOP_DOCUMENTS, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Neue Workshop-Dokumente verfügbar',
    content: `
            <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
            <p>für Ihren Workshop "{{product-name}}" wurden neue Dokumente hinzugefügt:</p>
            <p>{{document-links}}</p>
            <p>Sie können die Dokumente in Ihrem Workshop-Dashboard unter folgendem Link einsehen: {{login-link}}</p>
            <p>Mit freundlichen Grüßen</p>
            <p>---------------------------------------------------------------------</p>
            <p>Hello {{title}} {{firstname}} {{lastname}},</p>
            <p>new documents have been added to your workshop "{{product-name}}":</p>
            <p>{{document-links}}</p>
            <p>You can view the documents in your workshop dashboard using the following link: {{login-link}}</p>
            <p>Best regards</p>
            `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.NEW_WORKSHOP_DOCUMENTS),
  attachments: [],
  displayName: 'Neues Workshop-Dokument',
  editorHint: () => institutionMailHint(MailTemplateType.NEW_WORKSHOP_DOCUMENTS),
});

export async function notifyNewWorkshopDocument(participationID: string, documentIDs: string[]) {
  const participation = await getParticipationByID(participationID)({
    _id: 1,
    productID: 1,
    userID: 1,
    timeSlotID: 1,
  } as any);
  if (!participation) throw new Error('participation not found ' + participationID);

  const workshop = await getWorkshopByID(participation.productID)({
    _id: 1,
    name: 1,
    institutionID: 1,
    appointments: { start: 1, end: 1 },
  });
  if (!workshop) throw new Error('workshop not found ' + participation.productID);

  const workshopDocuments = await getWorkshopDocumentsByIDs(documentIDs)({
    _id: 1,
    title: 1,
    text: 1,
    fileID: 1,
    workshopID: 1,
  });
  if (!workshopDocuments) throw new Error('workshop document not found ' + participation.productID);
  const fileIDs = workshopDocuments.map(doc => doc.fileID);

  const institution = await getInstitutionByID(workshop.institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found ' + workshop.institutionID);

  const mailTemplateType = MailTemplateType.NEW_WORKSHOP_DOCUMENTS;

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    workshop.institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const user = await getUserByID(participation.userID)({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });
  if (!user) throw new Error('user not found ' + participation.userID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.PRODUCT_NAME]: workshop.name,
      [MailTemplateVars.DOCUMENT_LINKS]: fileIDs
        .map(fileID => AdornisFile.getServeLink(fileID))
        .map(link => `<a href="${link}">Hier herunterladen</a>`)
        .join('<br />'),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      institutionID: institution._id,
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
    });
  };

  // Get notification type
  const notificationType = await getNotificationType(user, mailTemplate, institution._id);

  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID: institution._id,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        workshop.name || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}

/**
 * Sends the "Neue Workshop-Dokumente verfügbar" mail to every participant the document is currently
 * visible to (based on its visibility flags). Deduped via the NewWorkshopDocumentNotification log, so
 * it is safe to call on every document save. Returns the number of mails sent.
 */
export async function notifyEligibleParticipantsForWorkshopDocument(documentID: string): Promise<number> {
  const documentCollection = mongo().collection(WorkshopDocument._collectionName);
  const document = (await documentCollection.findOne({ _id: documentID } as any)) as any;
  if (!document) return 0;

  const sharedWithSuccessful = !!document.sharedWithParticipantsWithSuccessfullParticipation;
  const sharedWithUnsuccessful = !!document.sharedWithParticipantsWithoutSuccessfullParticipation;
  if (!sharedWithSuccessful && !sharedWithUnsuccessful) return 0;

  const participationCollection = mongo().collection<Participation>(Participation._collectionName);
  const participations = await participationCollection
    .find({
      productID: document.workshopID,
      state: { $in: [ParticipationState.in, ParticipationState.participated] },
    })
    .toArray();

  // Only notify participants the document is actually shared with for their participation outcome.
  const eligible = participations.filter(
    p =>
      (p.state === ParticipationState.participated && sharedWithSuccessful) ||
      (p.state === ParticipationState.in && sharedWithUnsuccessful),
  );

  // Skip anyone already notified about this document (shared dedup log with the change-stream listener).
  const notificationCollection = mongo().collection(NewWorkshopDocumentNotification._collectionName);
  const existingNotifications = await notificationCollection
    .find({ _class: NewWorkshopDocumentNotification._class, documentID } as any)
    .toArray();
  const alreadyNotified = new Set(existingNotifications.map((n: any) => n.participationID as string));
  const toNotify = eligible.filter(p => !alreadyNotified.has(p._id));

  let sent = 0;
  for (const participation of toNotify) {
    try {
      await notifyNewWorkshopDocument(participation._id, [documentID]);
      await notificationCollection.insertOne({
        _class: NewWorkshopDocumentNotification._class,
        userID: participation.userID,
        documentID,
        participationID: participation._id,
        sentAt: new Date(),
      } as any);
      sent++;
    } catch (error) {
      logger.warn(
        { error, documentID, participationID: participation._id },
        'failed to send new workshop document notification',
      );
    }
  }

  return sent;
}
